import type { BaseRecord, DataProvider, HttpError } from "@refinedev/core";

import { getAccessToken } from "./auth";
import { restoreSession } from "./authProvider";

// spa-401-refresh: the in-memory access token can go missing mid-session — a startup race (a data call
// fires before restoreSession() has set the token), or the token simply expiring. Without recovery a
// single such call fails hard (→ onError → logout), which in practice showed up as one tab — the only
// one doing a *live* fetch — stuck on an error while the rest of the app served cached data. On a 401
// we silently re-mint the token from the HttpOnly refresh cookie (POST /api/auth/refresh) and retry the
// request once. Concurrent 401s share a single in-flight refresh so we don't stampede the endpoint.
let refreshInFlight: Promise<boolean> | null = null;
function refreshTokenOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = restoreSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// jve-2-4: full Refine DataProvider bound to the jve-1-6 / jve-7 API contract:
//   GET  /api/<resource>        -> array
//   GET  /api/<resource>/{id}   -> object
//   POST /api/<resource>        -> 201 created body
// The API does not yet send pagination/total headers, so getList reports the array length as total
// (client-side paging only). The bearer is attached from the in-memory token store (auth.ts) when
// present. HTTP failures are surfaced as Refine HttpError so screens get a typed { statusCode }.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function buildRequest(method: Method, body?: unknown): RequestInit {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return { method, headers, body: body === undefined ? undefined : JSON.stringify(body) };
}

async function httpFetch(path: string, method: Method = "GET", body?: unknown): Promise<unknown> {
  let response = await fetch(`${API_URL}${path}`, buildRequest(method, body));
  if (response.status === 401 && (await refreshTokenOnce())) {
    // Token re-minted from the refresh cookie — retry the original request once with the fresh bearer.
    response = await fetch(`${API_URL}${path}`, buildRequest(method, body));
  }
  if (!response.ok) {
    // Surface the server's response body as the error message when present (e.g. the products delete
    // 409 returns a plain-text koppelingen message; a JSON body may carry a `message` field). Falls
    // back to the status text so screens always get something to show.
    let message = response.statusText || `HTTP ${response.status}`;
    // A structured {message, errors[]} body (e.g. the communicatie publish 422 validation list) is
    // carried through on the HttpError so screens can render the per-problem `errors` inline, not just
    // the summary message.
    let errors: string[] | undefined;
    // A machine-readable `error` code (e.g. the API's "password_change_required" 403) — the status
    // alone can't tell those apart from a plain missing-authority denial, so it rides along and
    // authProvider.onError branches on it.
    let errorCode: string | undefined;
    // De geparste JSON-body, voor schermen die meer nodig hebben dan de tekst (bijv. een `code` en `details`).
    let body: unknown;
    try {
      const text = await response.text();
      if (text) {
        const trimmed = text.trim();
        if (trimmed.startsWith("{")) {
          const parsed = JSON.parse(trimmed) as { message?: string; errors?: unknown; error?: unknown };
          if (parsed.message) message = parsed.message;
          // RFC 7807 problem+json (e.g. the platform-pairing endpoints) carries its text in `detail`.
          else if (typeof (parsed as { detail?: unknown }).detail === "string") message = (parsed as { detail: string }).detail;
          // Klantportaal-endpoints antwoorden {code, melding, details}; de melding is de tekst.
          else if (typeof (parsed as { melding?: unknown }).melding === "string") message = (parsed as { melding: string }).melding;
          body = parsed;
          if (Array.isArray(parsed.errors)) errors = parsed.errors.map(String);
          if (typeof parsed.error === "string") errorCode = parsed.error;
        } else {
          message = trimmed;
        }
      }
    } catch {
      // keep the status-text fallback
    }
    const error: HttpError = { message, statusCode: response.status };
    // Carried under a custom key (not HttpError.errors, which is Refine's field-level ValidationErrors
    // map) so screens can render the server's flat per-problem list.
    if (errors) (error as HttpError & { validationErrors?: string[] }).validationErrors = errors;
    if (errorCode) (error as HttpError & { errorCode?: string }).errorCode = errorCode;
    if (body !== undefined) (error as HttpError & { body?: unknown }).body = body;
    throw error;
  }
  if (response.status === 204) {
    return undefined;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  // Refine's CRUD methods are generic over TData; the API returns plain JSON, so each result is cast
  // to `never` (assignable to any TData) — the same shape the jve-2-1 read slice used.
  getList: async ({ resource }) => {
    const body = await httpFetch(`/${resource}`);
    const data = Array.isArray(body) ? body : [];
    return { data: data as never[], total: data.length };
  },

  getOne: async ({ resource, id }) => {
    const body = await httpFetch(`/${resource}/${id}`);
    return { data: body as never };
  },

  getMany: async ({ resource, ids }) => {
    const all = await httpFetch(`/${resource}`);
    const rows = (Array.isArray(all) ? all : []) as BaseRecord[];
    const wanted = new Set(ids.map(String));
    return { data: rows.filter((r) => wanted.has(String(r.id))) as never[] };
  },

  create: async ({ resource, variables }) => {
    const body = await httpFetch(`/${resource}`, "POST", variables);
    return { data: body as never };
  },

  update: async ({ resource, id, variables }) => {
    const body = await httpFetch(`/${resource}/${id}`, "PUT", variables);
    return { data: (body ?? { id, ...(variables as object) }) as never };
  },

  deleteOne: async ({ resource, id }) => {
    const body = await httpFetch(`/${resource}/${id}`, "DELETE");
    return { data: (body ?? { id }) as never };
  },

  // `query` was silently dropped here, so a useCustom({ config: { query } }) fetched the endpoint
  // without its parameters and got an unfiltered — or empty — answer with a 200 to hide it. Empty and
  // null values are left out so an absent parameter stays absent rather than arriving as "".
  custom: async ({ url, method, payload, query }) => {
    const path = url.startsWith("/") ? url : `/${url}`;
    const params = new URLSearchParams();
    for (const [sleutel, waarde] of Object.entries(query ?? {})) {
      if (waarde !== undefined && waarde !== null && String(waarde) !== "") {
        params.set(sleutel, String(waarde));
      }
    }
    const zoekdeel = params.toString();
    const volledig = zoekdeel === "" ? path : `${path}${path.includes("?") ? "&" : "?"}${zoekdeel}`;
    const verb = (method ?? "get").toUpperCase() as Method;
    const body = await httpFetch(volledig, verb, verb === "GET" ? undefined : payload);
    return { data: body as never };
  },
};
